import { computed } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { settingsClient } from "@/services/api";
import { queryKeys } from "./queryKeys";
import { create } from "@bufbuild/protobuf";
import {
  ListSettingsRequestSchema,
  SetSettingRequestSchema,
} from "@/gen/tempus/v1/tempus_pb";

export function useSettingsQuery() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.settings.all(),
    queryFn: async () => {
      const resp = await settingsClient.listSettings(
        create(ListSettingsRequestSchema, {})
      );
      const map = new Map<string, string>();
      for (const s of resp.settings) {
        map.set(s.key, s.value);
      }
      return map;
    },
    staleTime: Infinity,
  });

  function getSetting(key: string): string | undefined {
    return query.data.value?.get(key);
  }

  const newBreedCategoryId = computed<number | null>({
    get() {
      const val = query.data.value?.get("new_breed_category_id");
      if (!val) return null;
      const id = parseInt(val, 10);
      return isNaN(id) ? null : id;
    },
    set(id: number | null) {
      setSettingMutation.mutate({
        key: "new_breed_category_id",
        value: id !== null ? String(id) : "",
      });
    },
  });

  const setSettingMutation = useMutation({
    mutationFn: async (variables: { key: string; value: string }) => {
      await settingsClient.setSetting(
        create(SetSettingRequestSchema, {
          key: variables.key,
          value: variables.value,
        })
      );
    },
    onMutate: async (variables) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.settings.all() });
      const previousData = queryClient.getQueryData<Map<string, string>>(
        queryKeys.settings.all()
      );
      queryClient.setQueryData<Map<string, string>>(
        queryKeys.settings.all(),
        (old) => {
          const newMap = new Map(old ?? []);
          newMap.set(variables.key, variables.value);
          return newMap;
        }
      );
      return { previousData };
    },
    onError: (_error, _variables, context) => {
      // Revert on failure
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.settings.all(), context.previousData);
      }
    },
  });

  return {
    ...query,
    settings: query.data,
    getSetting,
    newBreedCategoryId,
    setSetting: (key: string, value: string) =>
      setSettingMutation.mutate({ key, value }),
  };
}
